// Appeal-level DISPLAY names.
//
// WigleyHub stores generic ordinals — proposed, appeal_one … appeal_ten (APPEAL_KEY_WORDS in
// ParcelModal). That is deliberate: the storage carries no jurisdiction assumption. This file is
// the presentation layer over it, and nothing more — no migration, no schema change, no second
// home for the fact.
//
// ⛔ THIS TAXONOMY IS JURISDICTION-DEPENDENT AND THE FLAT MAP IS A CALIFORNIA ANSWER.
// The real vocabulary varies by state, and the differences are statutory, not cosmetic:
//   • Texas   — "2525C" is a reference to Tax Code §25.25(c), a correction procedure with no
//               equivalent elsewhere.
//   • Illinois— "State Level" means the Property Tax Appeal Board (PTAB), a distinct body from a
//               county board of review.
//   • California — the ladder below (informal → assessment appeals board → arbitration →
//               litigation) is what the analyst's reports use, and CA is all we hold today.
// A parcel in Texas mapped through this table would read "Board" where the document should say
// "2525C", which is wrong in a way a client would notice.
//
// THE RIGHT LONG-TERM HOME is lookup_options keyed by state — the same managed-list machinery the
// app already uses for Parcel Status and Appeal Status, resolved per parcel's state at render.
// That is a build, not a rename, and it should wait until a second state's data actually lands.
// Until then a flat map is honest: one jurisdiction, one vocabulary, stated in one place.
//
// ⛔ STATUS IS NOT MAPPED, HERE OR ANYWHERE. WigleyHub's appeal_status vocabulary — Value Pending,
// Under Review, Value Accepted, Appeal Open, Withdrawn — IS the client-facing wording, on screen,
// in Excel and in the PDF alike. A translation layer would let stored and displayed status
// diverge, and the first time they disagreed nobody would know which was true. A status that
// needs renaming gets renamed in the data.

export const APPEAL_LEVEL_LABEL = {
  proposed: 'Proposed',
  appeal_one: 'Informal',
  appeal_two: 'Board',
  appeal_three: 'Binding Arbitration',
  appeal_four: 'Litigation',
};

// Unmapped ordinals (appeal_five and beyond) fall through to a readable form rather than a blank
// or a raw key — "Appeal 5" is at least legible if the ladder ever runs deeper than the map.
export function appealLevelLabel(level) {
  if (!level) return '';
  if (APPEAL_LEVEL_LABEL[level]) return APPEAL_LEVEL_LABEL[level];
  const m = /^appeal_(\w+)$/.exec(level);
  if (!m) return level;
  const words = ['one', 'two', 'three', 'four', 'five', 'six', 'seven', 'eight', 'nine', 'ten'];
  const n = words.indexOf(m[1]);
  return n >= 0 ? `Appeal ${n + 1}` : level;
}

// "Board - Appeal Open" — the mapped LEVEL with the RAW status, used by the PDF and anywhere else
// the two are shown as one string. Either half may be missing; the separator only appears between
// two present values.
export const levelStatusLabel = (level, status) =>
  [appealLevelLabel(level), status].filter(Boolean).join(' - ');
